if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();

      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      };

      this.mql = window.matchMedia('(min-width: 750px)');
      this.productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
      if (this.productInfo && Shopify.postLinksRetry) this.productInfo.initShareLinks();
      if (!this.elements.viewer) return;

      // Do not depend on a global debounce helper. In this theme it is not defined,
      // which prevents the constructor from reaching the thumbnail event binding.
      this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));
      this.bindThumbnailEvents();

      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
        this.removeListSemantic();
      }
    }

    bindThumbnailEvents() {
      const container = this.elements.thumbnails;
      if (!container || container.dataset.mediaGalleryTapBound === 'true') return;
      container.dataset.mediaGalleryTapBound = 'true';

      let pointerState = null;
      let touchHandledAt = 0;

      const getButton = (target) => target && target.closest ? target.closest('button.thumbnail') : null;

      const activate = (button) => {
        if (!button || !container.contains(button)) return;
        const item = button.closest('[data-target]');
        const mediaId = item && item.dataset.target;
        if (!mediaId) return;
        this.setActiveMedia(mediaId, false);
      };

      // Delegated pointer handling: a short, stationary touch is a TAP.
      // A horizontal drag remains native scrolling and does not select a thumbnail.
      container.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse') return;
        const button = getButton(event.target);
        if (!button || !container.contains(button)) return;
        pointerState = {
          button,
          x: event.clientX,
          y: event.clientY,
          time: performance.now()
        };
      }, { passive: true });

      container.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' || !pointerState) return;
        const endButton = getButton(event.target);
        const dx = Math.abs(event.clientX - pointerState.x);
        const dy = Math.abs(event.clientY - pointerState.y);
        const duration = performance.now() - pointerState.time;
        const isTap = endButton === pointerState.button && dx < 14 && dy < 14 && duration < 700;
        const button = pointerState.button;
        pointerState = null;

        if (!isTap) return;
        touchHandledAt = Date.now();
        activate(button);
      }, { passive: true });

      container.addEventListener('pointercancel', () => {
        pointerState = null;
      }, { passive: true });

      // Mouse/keyboard and browser click fallback. Ignore the synthetic click that
      // commonly follows a touch pointerup to prevent duplicate activation.
      container.addEventListener('click', (event) => {
        const button = getButton(event.target);
        if (!button || !container.contains(button)) return;
        if (Date.now() - touchHandledAt < 600) return;
        activate(button);
      });
    }

    onSlideChanged(event) {
      const currentElement = event.detail && event.detail.currentElement;
      if (!currentElement) return;
      const mediaId = currentElement.dataset.mediaId;
      if (!mediaId) return;
      const thumbnail = this.elements.thumbnails
        ? this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`)
        : null;
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      if (!mediaId || !this.elements.viewer) return;

      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
      if (!activeMedia) {
        console.warn('[MediaGallery] No media found:', mediaId);
        return;
      }

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          if (activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider && typeof this.elements.viewer.resetPages === 'function') {
          this.elements.viewer.resetPages();
        }
      }

      this.preventStickyHeader();

      window.requestAnimationFrame(() => {
        if (this.elements.viewer.slider) {
          this.elements.viewer.slider.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
        } else {
          activeMedia.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
      });

      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
      if (!activeThumbnail) return;
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button.thumbnail').forEach((element) => {
        element.removeAttribute('aria-current');
      });

      const button = thumbnail.querySelector('button.thumbnail');
      if (!button) return;
      button.setAttribute('aria-current', 'true');

      if (
        typeof this.elements.thumbnails.isSlideVisible === 'function' &&
        this.elements.thumbnails.isSlideVisible(thumbnail, 10)
      ) {
        return;
      }

      if (this.elements.thumbnails.slider) {
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
      } else {
        thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }

    announceLiveRegion(activeItem, position) {
      if (!this.elements.liveRegion || !position) return;
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      if (window.pauseAllMedia) window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia && typeof deferredMedia.loadContent === 'function') deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      if (this.elements.viewer.sliderItems) {
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  });
}
